import { NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Route,
  Router,
  RouterOutlet,
  Routes
} from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import {
  AxleButtonComponent,
  AxleSpinnerComponent
} from '@axleaccess/axle-components';
import { EditorModule } from '@tinymce/tinymce-angular';
import posthog from 'posthog-js';
import { catchError, debounceTime, filter, map } from 'rxjs';

import { AxleDevBackendOverrideComponent } from 'src/app/shared/components/dev-backend-override/axle-dev-backend-override.component';
import { dismissedDowntimeBannerStorageKey } from 'src/app/shared/constants/downtime-banner.constants';
import {
  createDowntimeBannerDismissal,
  isDowntimeBannerDismissal,
  isDowntimeBannerDismissed,
  isDowntimeBannerExpired,
  scheduleAt
} from 'src/app/shared/functions/downtime-banner.functions';
import {
  getLocalStorageJson,
  removeLocalStorageItem,
  setLocalStorageJson
} from 'src/app/shared/functions/storage.functions';
import {
  DowntimeBanner,
  DowntimeBannerDismissal
} from 'src/app/shared/models/downtime-banner.models';
import { RouterService } from 'src/app/shared/services/router/router.service';
import {
  SIDE_DRAWER_CONFIG,
  SideDrawerKey
} from 'src/app/shared/services/side-drawer/side-drawer.constants';
import { SideDrawerService } from 'src/app/shared/services/side-drawer/side-drawer.service';
import { AxleToastService } from 'src/app/shared/services/toast/axle-toast.service';
import { FeatureFlag } from 'src/app/shared/services/user/user.models';
import { UserStore } from 'src/app/shared/services/user/user.state';
import { isIdpInitiatedFlow } from 'src/app/shared/utils/auth0-idp.utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'axle-app',
  imports: [
    AxleButtonComponent,
    RouterOutlet,
    EditorModule,
    NgClass,
    AxleSpinnerComponent,
    AxleDevBackendOverrideComponent
  ],
  templateUrl: './axle-app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  private _activatedRoute = inject(ActivatedRoute);
  private _destroyRef = inject(DestroyRef);
  private _router = inject(Router);
  private _routerService = inject(RouterService);
  private _sideDrawerService = inject(SideDrawerService);
  private _toastService = inject(AxleToastService);
  private _userStore = inject(UserStore);
  private _auth0Service = inject(AuthService);

  hideIntegrationBanner = signal<boolean>(false);
  idpInitiatedLoading = signal<boolean>(false);
  private _dismissedDowntimeBanner = signal<DowntimeBannerDismissal | null>(
    getLocalStorageJson(
      dismissedDowntimeBannerStorageKey,
      isDowntimeBannerDismissal
    )
  );
  private _downtimeBannerExpired = signal(false);

  showProgressBar = toSignal(
    this._router.events.pipe(
      debounceTime(300),
      map((event) => {
        if (event instanceof NavigationStart) {
          return true;
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError ||
          event instanceof NavigationSkipped
        ) {
          return false;
        } else {
          return true;
        }
      })
    )
  );
  private _queryParams = toSignal(this._activatedRoute.queryParams);

  toasts = this._toastService.toasts;
  showDevBackendOverride = environment.env === 'dev';
  private _handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      posthog.reloadFeatureFlags();
    }
  };
  private _hasVisitedPublic = false;

  visibleDowntimeBanner = computed<DowntimeBanner | null>(() => {
    const banner = this._userStore.downtimeBanner();
    if (!banner) return null;

    if (
      this._downtimeBannerExpired() ||
      isDowntimeBannerExpired(banner) ||
      isDowntimeBannerDismissed(banner, this._dismissedDowntimeBanner())
    )
      return null;

    return banner;
  });

  googleIntegrationRevoked = computed(
    () => this._userStore.userInfo()?.integrationStatusInvalid.google
  );

  outlookIntegrationRevoked = computed(
    () => this._userStore.userInfo()?.integrationStatusInvalid.outlook
  );

  zoomIntegrationRevoked = computed(
    () => this._userStore.userInfo()?.integrationStatusInvalid.zoom
  );

  constructor() {
    effect(() => {
      const sideDrawerKey = this._queryParams()?.['side_drawer'] as
        | SideDrawerKey
        | undefined;
      if (sideDrawerKey && SIDE_DRAWER_CONFIG[sideDrawerKey]?.autoOpen) {
        this._sideDrawerService.openSideDrawer(sideDrawerKey, undefined, () =>
          this._routerService.removeQueryParam('side_drawer')
        );
      }
    });

    effect((onCleanup) => {
      const dismissal = this._dismissedDowntimeBanner();
      if (!dismissal) {
        removeLocalStorageItem(dismissedDowntimeBannerStorageKey);
        return;
      }

      setLocalStorageJson(dismissedDowntimeBannerStorageKey, dismissal);
      if (!dismissal.dismissedUntil) return;

      onCleanup(
        scheduleAt(dismissal.dismissedUntil, () =>
          this._dismissedDowntimeBanner.set(null)
        )
      );
    });

    effect((onCleanup) => {
      const banner = this._userStore.downtimeBanner();
      if (!banner?.endsAt) {
        this._downtimeBannerExpired.set(false);
        return;
      }

      this._downtimeBannerExpired.set(false);
      onCleanup(
        scheduleAt(banner.endsAt, () => this._downtimeBannerExpired.set(true))
      );
    });

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        if (event.url.startsWith('/public') && !this._hasVisitedPublic) {
          this._hasVisitedPublic = true;
          this._preloadPublicRoutes();
        }
      });
  }

  ngOnInit() {
    if (isIdpInitiatedFlow()) {
      // IdP-initiated flow: handle the redirect
      this._handleIdpInitiatedFlow();
      return;
    }

    this._initializeApp();
  }

  ngOnDestroy() {
    document.removeEventListener(
      'visibilitychange',
      this._handleVisibilityChange
    );
  }

  goToPreferences() {
    this.hideIntegrationBanner.set(true);
    this._router.navigate(['/preferences']);
  }

  dismissDowntimeBanner(banner: DowntimeBanner) {
    this._dismissedDowntimeBanner.set(createDowntimeBannerDismissal(banner));
  }

  removeToast(index: number) {
    this._toastService.remove(index);
  }

  handleToastAction(index: number, action: () => void) {
    action();
    this._toastService.remove(index);
  }

  private _handleIdpInitiatedFlow() {
    // IdP-initiated flow returns tokens in hash (implicit flow)
    // We need to discard these and get proper tokens with our audience
    // Clear the hash immediately
    window.location.hash = '';

    // Show loading spinner
    this.idpInitiatedLoading.set(true);

    // Mark this as an IDP-initiated flow in sessionStorage
    // so the guard can allow it through during the redirect callback
    sessionStorage.setItem('idpInitiatedFlow', 'true');

    // Redirect to Auth0 to get tokens with the correct audience
    this._auth0Service
      .loginWithRedirect({
        authorizationParams: {
          audience: environment.auth0.authorizationParams?.['audience'],
          scope: 'openid profile email offline_access',
          prompt: 'none' // Try SSO first
        },
        appState: {
          target: '/',
          idpInitiated: true
        }
      })
      .pipe(
        catchError((error: Error) => {
          // Fallback: If silent auth fails (e.g., Auth0 session expired),
          // retry with interactive login to re-establish the session
          console.warn(
            'Silent auth failed during IdP-initiated flow, falling back to interactive login:',
            error
          );
          sessionStorage.removeItem('idpInitiatedFlow');
          return this._auth0Service.loginWithRedirect({
            authorizationParams: {
              audience: environment.auth0.authorizationParams?.['audience'],
              scope: 'openid profile email offline_access'
            },
            appState: {
              target: '/',
              idpInitiated: true
            }
          });
        })
      )
      .subscribe();
  }

  private _initializeApp() {
    posthog.onFeatureFlags((flags) =>
      this._userStore.storeFeatureFlags(new Set(flags as FeatureFlag[]))
    );

    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }

  private async _preloadChildren(routes: Routes): Promise<void> {
    const BATCH_SIZE = 3;
    const BATCH_DELAY = 200;

    for (let i = 0; i < routes.length; i += BATCH_SIZE) {
      const batch = routes.slice(i, i + BATCH_SIZE);
      const promises = batch.map((route) => this._preloadSingleRoute(route));

      await Promise.allSettled(promises);

      if (i + BATCH_SIZE < routes.length) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY));
      }
    }
  }

  private async _preloadPublicRoutes(): Promise<void> {
    const allRoutes = this._router.config;
    const publicRoute = allRoutes.find((route) => route.path === 'public');

    if (publicRoute?.children) {
      await this._preloadChildren(publicRoute.children);
    }
  }

  private async _preloadSingleRoute(route: Route): Promise<void> {
    try {
      if (route.loadComponent) {
        await route.loadComponent();
      }

      if (route.loadChildren) {
        await route.loadChildren();
      }

      if (route.children) {
        await this._preloadChildren(route.children);
      }
    } catch (error) {
      if (!environment.production) {
        console.warn(`Failed to preload route: ${route.path}`, error);
      }
    }
  }
}
